/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
  TOKEN_EXPIRED_OR_INVALID_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Company Edit logic module.
 * Handles company profile editing operations including reference data retrieval,
 * company updates, file uploads to S3, and profile completion calculations.
 * @module logic/companyEdit
 */

import { refreshToken, signOut } from "../../utils/common.js";
import {
  getGenericErrorMessage,
} from "../../utils/genericErrorMessages.js";

// Constants for duplicated literals

/**
 * Fetches reference data from the backend based on the provided type
 * @param {string} type - The type of reference data to fetch (e.g., 'countries', 'states', etc.)
 * @param {Function} navigate - Navigation function from react-router
 * @returns {Promise<Array>} - Array of reference data items
 */
export const getReferenceData = async (type, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}referenceData/${type}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getReferenceData(type, navigate);
    }
    if (Array.isArray(data.data)) {
      return data.data.sort((a, b) => {
        if (type === "zipCodes") {
          return a.codigo.localeCompare(b.codigo, undefined, { numeric: true });
        } else {
          return a.nombre.localeCompare(b.nombre, undefined, {
            sensitivity: "base",
          });
        }
      });
    }

    return [];
  } catch (error) {
    logger.error(`Error fetching ${type} data:`, { err: sanitizeError(error) });
    return [];
  }
};

export const getReferenceDataById = async (type, id, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}referenceData/${type}/${id}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getReferenceDataById(type, id, navigate);
    }

    return data;
  } catch (error) {
    logger.error(`Error fetching ${type} data:`, { err: sanitizeError(error) });
    return null;
  }
};

/**
 * Fetches the current user's permissions
 * @param {string} userId
 * @param {Function} navigate
 * @returns {Promise<Array|null>} permisos array or null on error
 */
export const getUserPermissions = async (userId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}permission/${userId}`,
      options,
    );
    const roleData = await response.json();

    const isTokenExpired =
      roleData?.message === TOKEN_EXPIRED_MESSAGE ||
      roleData?.message === TOKEN_EXPIRED_OR_INVALID_MESSAGE;
    if (isTokenExpired) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getUserPermissions(userId, navigate);
    }

    if (roleData?.message) {
      return null;
    }
    return roleData?.permisos ?? [];
  } catch (error) {
    logger.error("Error fetching user permissions:", {
      err: sanitizeError(error),
    });
    return null;
  }
};

/**
 * Fetch company details by id
 * @param {string} companyId
 * @param {Function} navigate
 * @returns {Promise<Object|null>} company
 */
export const getCompanyById = async (companyId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}company/${companyId}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getCompanyById(companyId, navigate);
    }

    if (!response.ok) {
      return null;
    }
    return data;
  } catch (error) {
    logger.error("Error fetching company details:", {
      err: sanitizeError(error),
    });
    return null;
  }
};

/**
 * Returns how many of the key profile attributes are filled to drive the progress indicator
 * @param {Object} company
 * @param {number} selectedProfile - 0 for main profile, 1 for profile 2, 2 for profile 3
 * @returns {number}
 */
export const calculateProgressStep = (company, selectedProfile = 0) => {
  // Helper to find profile by numeroDePerfil (more robust than array index)
  const getProfileByNumber = (profileNumber) => {
    if (!company?.perfiles || !Array.isArray(company.perfiles)) {
      return null;
    }
    return (
      company.perfiles.find((p) => p?.numeroDePerfil === profileNumber) || null
    );
  };

  // Helper to get profile-specific or main company value
  const getFieldValue = (fieldName) => {
    if (selectedProfile === 0) {
      return company?.[fieldName];
    }
    // For profiles 1 or 2, find by numeroDePerfil instead of array index
    const profile = getProfileByNumber(selectedProfile);
    return profile?.[fieldName];
  };

  // Profile-editable fields come from the selected profile
  // Logo is not profile-editable, so it always comes from the main company
  const attributes = [
    getFieldValue("descripcionDeLaEmpresa"),
    getFieldValue("procesosYEspecialidades"),
    getFieldValue("brochure"),
    company?.logo, // Logo is always from main company (not profile-editable)
  ];

  const isAttributeFilled = (attr) => {
    if (!attr) {
      return false;
    }

    // Handle string values (URLs, simple text)
    if (typeof attr === "string") {
      return attr.trim() !== "";
    }

    // Handle multi-language objects like { en: "...", es: "...", fr: "..." }
    if (typeof attr === "object" && !Array.isArray(attr)) {
      return Object.values(attr).some(
        (value) => typeof value === "string" && value.trim() !== "",
      );
    }

    return false;
  };

  return attributes.filter(isAttributeFilled).length;
};

/**
 * Document keys that require presigned URL upload
 */
export const DOCUMENT_KEYS = [
  "documentos",
  "brochure",
  "cedulaFiscal",
  "opinionDeCumplimientoPositiva",
  "polizaDeResponsabilidadCivil",
  "altaIMSS",
  "REPSE",
  "comprobanteDeCertificacion",
  "logo",
];

/**
 * Flat company fields for additional contact persons (contacto1 = main; 2–10 = additional).
 * Nulls for these must be sent so the API can clear removed contacts; other nulls stay omitted.
 */
const ADDITIONAL_CONTACTO_FLAT_FIELD = /^contacto(10|[2-9])/;

/**
 * Split formData into regular and file fields
 * @param {Object} formData
 * @param {Array<string>} documentKeys
 * @returns {{regularFields: Object, fileFields: Object}}
 */
export const splitFormDataForUpdate = (
  formData,
  documentKeys = DOCUMENT_KEYS,
) => {
  const regularFields = {};
  const fileFields = {};

  Object.entries(formData || {}).forEach(([section, sectionData]) => {
    Object.entries(sectionData || {}).forEach(([field, value]) => {
      if (value === null) {
        if (ADDITIONAL_CONTACTO_FLAT_FIELD.test(field)) {
          regularFields[field] = null;
        }
        return;
      }

      if (documentKeys.includes(field)) {
        fileFields[field] = {
          fieldName: field,
          fileName: value?.name ?? field,
          file: value,
        };
      } else {
        regularFields[field] = value;
      }
    });
  });

  return { regularFields, fileFields };
};

/**
 * Split companyData into regular and file fields
 * @param {Object} companyData
 * @param {Array<string>} documentKeys
 * @returns {{regularFields: Object, fileFields: Object}}
 */
export const splitCompanyDataForUpdate = (
  companyData,
  documentKeys = DOCUMENT_KEYS,
) => {
  const companyRegularFields = {};
  const companyFileFields = {};
  Object.entries(companyData || {}).forEach(([field, value]) => {
    if (value === null) {
      return;
    }

    if (documentKeys.includes(field)) {
      // Existing company docs are S3 path strings. New uploads are File objects
      // (already covered by splitFormDataForUpdate / form fileFields).
      if (typeof value !== "string") {
        return;
      }
      const filename = value.split("/").pop();
      companyFileFields[field] = {
        fieldName: field,
        fileName: filename ?? field,
        file: value,
      };
    } else {
      companyRegularFields[field] = value;
    }
  });

  return { companyRegularFields, companyFileFields };
};

/**
 * Update company with regular fields and request presigned URLs for file fields
 * @param {string} companyId
 * @param {Object} regularFields
 * @param {Object} fileFields
 * @param {Function} navigate
 * @param {number} selectedProfile - 0 for main company, 1 for profile 2, 2 for profile 3
 * @returns {Promise<{company: Object, presignedUrls?: Object}>}
 */
export const updateCompany = async (
  companyId,
  regularFields,
  fileFields,
  navigate,
  selectedProfile = 0,
) => {
  const doRequest = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}company/${companyId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": CONTENT_TYPE_JSON,
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          ...regularFields,
          fileFields: Object.values(fileFields || {}),
          perfilSeleccionado: selectedProfile,
        }),
      },
    );

    const data = await response.json();
    return { ok: response.ok, data };
  };

  try {
    const { ok, data } = await doRequest();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        throw new Error(getGenericErrorMessage("TOKEN_EXPIRED_REFRESH_FAILED"));
      }
      const retry = await doRequest();
      if (!retry.ok) {
        throw new Error(getGenericErrorMessage("FAILED_TO_UPDATE_COMPANY"));
      }
      return retry.data;
    }

    if (!ok) {
      throw new Error(getGenericErrorMessage("FAILED_TO_UPDATE_COMPANY"));
    }

    return data;
  } catch (error) {
    logger.error("Error updating company:", { err: sanitizeError(error) });
    throw error;
  }
};

/**
 * Upload files to S3 using presigned URLs
 * @param {Object} presignedUrls
 * @param {Object} fileFields
 * @returns {Promise<Array<{field: string, success: boolean}>>}
 */
export const uploadCompanyFiles = async (presignedUrls, fileFields) => {
  try {
    const uploadPromises = [];

    for (const [field, fileData] of Object.entries(fileFields || {})) {
      if (!fileData || !presignedUrls?.[field]) {
        continue;
      }

      const presignedUrl = presignedUrls[field];
      const file = fileData.file;

      const uploadPromise = fetch(presignedUrl.uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file?.type || "application/octet-stream",
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              getGenericErrorMessage("FAILED_TO_UPLOAD_FIELD_TO_S3", {
                field,
              }),
            );
          }
          return { field, success: true };
        })
        .catch((error) => {
          logger.error(`Error uploading ${field}:`, {
            err: sanitizeError(error),
          });
          return { field, success: false, error };
        });

      uploadPromises.push(uploadPromise);
    }

    return await Promise.all(uploadPromises);
  } catch (error) {
    logger.error("Error handling file uploads:", { err: sanitizeError(error) });
    throw error;
  }
};
